import {
  createElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
} from 'react';
import { InputAdornment, TextField, makeStyles } from '@material-ui/core';
import { Templates } from '@rjsf/material-ui';
import type { WidgetProps } from '@rjsf/utils';
import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type {
  FieldExtensionComponentProps,
  FieldExtensionOptions,
} from '@backstage/plugin-scaffolder-react';
import { repoLocationOf } from './templates';

/**
 * How the panel draws a template's fields (DX-650): the template engine's own
 * fields, with two of them drawn the way the portal's other forms are — and only
 * where a template asks for exactly what that drawing can answer. A template
 * asking for anything else gets the engine's field, so a template stays
 * portable and nothing here changes what a field returns.
 */

const useStyles = makeStyles(theme => ({
  note: {
    display: 'block',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-nav-active-bg)',
    color: 'var(--ui-text)',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  mono: { fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' },
  prefix: {
    marginRight: 0,
    fontFamily: 'var(--font-mono)',
    fontSize: '0.875rem',
    color: 'var(--ui-text-muted)',
    whiteSpace: 'nowrap',
  },
  repo: { fontFamily: 'var(--font-mono)', fontSize: '0.875rem' },
  // Flush with the field, as the engine writes a field's description under it.
  helper: { marginLeft: 0, marginRight: 0 },
}));

type Field = ComponentType<FieldExtensionComponentProps<any, any>>;

/**
 * The text inputs of a template's form, outlined as the panel's other fields
 * are. The theme's own input with one difference: the engine draws it
 * underlined, which no other form in the portal does.
 */
export const OutlinedInput = (props: WidgetProps) => {
  const Base = Templates.BaseInputTemplate as ComponentType<WidgetProps>;
  return <Base {...props} variant="outlined" size="small" />;
};

type CatalogFilter = Record<string, unknown>;

type OwnerOptions = {
  catalogFilter?: CatalogFilter | CatalogFilter[];
  allowArbitraryValues?: boolean;
};

const filtersOf = (options?: OwnerOptions) =>
  [options?.catalogFilter ?? []].flat();

/**
 * An owner picker offering a fixed set of teams: free text refused, and a
 * filter that asks for groups by plain values only. That is a choice between
 * known teams, which a select answers better than a search box — anything else
 * (people, `exists`, typed values) stays the engine's picker.
 */
export const offersTeams = (options?: OwnerOptions) => {
  const filters = filtersOf(options);
  return (
    options?.allowArbitraryValues === false &&
    filters.length > 0 &&
    filters.every(filter => {
      const kinds = [filter.kind ?? []].flat();
      return (
        kinds.length > 0 &&
        kinds.every(kind => String(kind).toLowerCase() === 'group') &&
        Object.values(filter).every(value =>
          [value].flat().every(part => typeof part === 'string'),
        )
      );
    })
  );
};

type Team = { ref: string; name: string; handle?: string };

const teamOf = (entity: Entity): Team => {
  const profile = entity.spec?.profile as { displayName?: string } | undefined;
  return {
    ref: stringifyEntityRef(entity),
    name: profile?.displayName ?? entity.metadata.title ?? entity.metadata.name,
    // Written by the GitHub org provider: the team's handle on GitHub, which is
    // how CODEOWNERS names it.
    handle: entity.metadata.annotations?.['github.com/team-slug'],
  };
};

type Teams = {
  loading: boolean;
  failed: boolean;
  mine: Team[];
  others: Team[];
};

/** The teams the filter offers, the signed-in colleague's own first. */
function useTeams(options?: OwnerOptions): Teams {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const asked = JSON.stringify(filtersOf(options));
  const [teams, setTeams] = useState<Teams>({
    loading: true,
    failed: false,
    mine: [],
    others: [],
  });

  useEffect(() => {
    let live = true;
    Promise.all([
      catalogApi.getEntities({ filter: JSON.parse(asked) }),
      identityApi.getBackstageIdentity().then(
        identity => identity.ownershipEntityRefs,
        () => [] as string[],
      ),
    ]).then(
      ([{ items }, owned]) => {
        if (!live) return;
        const all = items
          .map(teamOf)
          .sort((a, b) => a.name.localeCompare(b.name, 'en'));
        setTeams({
          loading: false,
          failed: false,
          mine: all.filter(team => owned.includes(team.ref)),
          others: all.filter(team => !owned.includes(team.ref)),
        });
      },
      () => {
        if (live)
          setTeams({ loading: false, failed: true, mine: [], others: [] });
      },
    );
    return () => {
      live = false;
    };
  }, [catalogApi, identityApi, asked]);

  return teams;
}

/**
 * The owning team, as a choice between the teams by name (DX-650). The value is
 * the team's entity ref, exactly what the engine's picker answers with, so the
 * template and what the server accepts are untouched (DX-605). Nothing is
 * chosen for the reader: their own team is listed first, not picked (ADR-0104).
 */
export const TeamSelect = ({
  schema,
  uiSchema,
  formData,
  onChange,
  required,
  rawErrors,
  idSchema,
  disabled,
}: FieldExtensionComponentProps<string, OwnerOptions>) => {
  const classes = useStyles();
  const teams = useTeams(uiSchema?.['ui:options']);
  const all = [...teams.mine, ...teams.others];
  const picked = all.find(team => team.ref === formData);
  // A choice with nothing to choose reads as a broken field, so it says why.
  let problem: string | undefined;
  if (teams.failed) problem = "Couldn't load the teams from the catalog.";
  else if (!teams.loading && all.length === 0) {
    problem = 'The catalog holds none of the teams this template offers.';
  }
  const option = (team: Team) => (
    <option key={team.ref} value={team.ref}>
      {team.name}
    </option>
  );

  return (
    <>
      <TextField
        select
        id={idSchema?.$id}
        label={schema.title ?? 'Owner'}
        value={formData ?? ''}
        onChange={event => onChange(event.target.value || undefined)}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        FormHelperTextProps={{ className: classes.helper }}
        variant="outlined"
        size="small"
        fullWidth
        required={required}
        disabled={disabled || teams.loading}
        error={(rawErrors?.length ?? 0) > 0 || Boolean(problem)}
        helperText={problem}
      >
        <option value="" disabled>
          {teams.loading ? 'Loading…' : 'Choose a team…'}
        </option>
        {teams.mine.length > 0 && (
          <optgroup
            label={teams.mine.length === 1 ? 'Your team' : 'Your teams'}
          >
            {teams.mine.map(option)}
          </optgroup>
        )}
        {teams.mine.length > 0 && teams.others.length > 0 && (
          <optgroup label="Other teams">{teams.others.map(option)}</optgroup>
        )}
        {teams.mine.length === 0 && teams.others.map(option)}
      </TextField>
      {picked && (
        <span role="status" className={classes.note}>
          <strong>{picked.name}</strong> becomes the owner:{' '}
          <span className={classes.mono}>{picked.ref}</span> in the catalog
          {picked.handle && (
            <>
              , <span className={classes.mono}>@{picked.handle}</span> on GitHub
            </>
          )}
          .
        </span>
      )}
    </>
  );
};

type RepoOptions = {
  allowedHosts?: string[];
  allowedOwners?: string[];
  allowedOrganizations?: string[];
  allowedProjects?: string[];
  allowedRepos?: string[];
  requestUserCredentials?: unknown;
};

/**
 * A repository picker with one place to put it — one host, one owner — so the
 * only thing left to ask is the name. Anything wider stays the engine's picker.
 */
export const hasOneDestination = (options?: RepoOptions) =>
  options?.allowedHosts?.length === 1 &&
  options.allowedOwners?.length === 1 &&
  !options.allowedOrganizations?.length &&
  !options.allowedProjects?.length &&
  !options.allowedRepos?.length &&
  !options.requestUserCredentials;

/** The repository name out of the engine's `host?owner=…&repo=…` value. */
export const repoNameOf = (value?: string) => repoLocationOf(value).repo;

/**
 * The repository, as one field with its host and owner written in front
 * (DX-650) — instead of a host, an owner and a name, two of which are fixed.
 * It writes the value the engine's picker writes, so the engine's validation of
 * it still runs. While the reader has not typed a repository name of their own,
 * it follows the form's `name`.
 */
export const RepositoryName = ({
  schema,
  uiSchema,
  formData,
  onChange,
  required,
  rawErrors,
  idSchema,
  disabled,
  formContext,
}: FieldExtensionComponentProps<string, RepoOptions>) => {
  const classes = useStyles();
  const options = uiSchema?.['ui:options'];
  const host = options?.allowedHosts?.[0] ?? '';
  const owner = options?.allowedOwners?.[0] ?? '';
  const repo = repoNameOf(formData);

  const write = useCallback(
    (name: string) =>
      // Nothing rather than a value without a name: the form then says the
      // field is required instead of the engine's "incomplete location".
      onChange(
        name
          ? `${host}?${new URLSearchParams({ owner, repo: name })}`
          : undefined,
      ),
    [host, owner, onChange],
  );

  const name =
    typeof formContext?.formData?.name === 'string'
      ? formContext.formData.name
      : '';
  const followed = useRef<string>();
  useEffect(() => {
    const previous = followed.current;
    followed.current = name;
    if (name === previous) return;
    if (!repo || repo === previous) write(name);
  }, [name, repo, write]);

  return (
    <TextField
      id={idSchema?.$id}
      label={schema.title ?? 'Repository'}
      value={repo}
      onChange={event => write(event.target.value)}
      variant="outlined"
      size="small"
      fullWidth
      required={required}
      disabled={disabled}
      error={(rawErrors?.length ?? 0) > 0}
      InputLabelProps={{ shrink: true }}
      FormHelperTextProps={{ className: classes.helper }}
      InputProps={{
        className: classes.repo,
        startAdornment: (
          <InputAdornment
            position="start"
            disableTypography
            className={classes.prefix}
          >
            {host}/{owner}/
          </InputAdornment>
        ),
      }}
      inputProps={{ spellCheck: false, autoComplete: 'off' }}
      helperText={name ? 'Follows the name until you change it.' : undefined}
    />
  );
};

/** Draws `ours` where `applies` holds for the field's options, else `stock`. */
const presentAs =
  (stock: Field, ours: Field, applies: (options?: any) => boolean): Field =>
  props =>
    createElement(
      applies(props.uiSchema?.['ui:options']) ? ours : stock,
      props,
    );

/**
 * The fields the panel's form is given: every field extension the scaffolder
 * knows, with the owner and repository pickers drawn as above where they apply.
 * The validations stay keyed by the same names, so they still run.
 */
export function panelFields(
  extensions: FieldExtensionOptions[],
): Record<string, Field> {
  const fields: Record<string, Field> = Object.fromEntries(
    extensions.map(({ name, component }) => [name, component as Field]),
  );
  if (fields.OwnerPicker) {
    fields.OwnerPicker = presentAs(fields.OwnerPicker, TeamSelect, offersTeams);
  }
  if (fields.RepoUrlPicker) {
    fields.RepoUrlPicker = presentAs(
      fields.RepoUrlPicker,
      RepositoryName,
      hasOneDestination,
    );
  }
  return fields;
}
